/*
 * 時刻・経過時間 に対するHuman Read/Writable な表現から、内部表現に変換する
 * ３桁の時間に対応
 *
 * ## 時刻
 *
 * ### 受け付ける形式
 * * HHH:MM
 * * HH:MM (出力時の標準)
 * * H:MM
 * * HHHMM
 * * HHMM
 * * HMM
 *
 * ### 内部形式
 * HH:MM
 *
 *
 * ## 経過時間
 *
 * ### 受け付ける形式
 * * HHH:MM
 * * HH:MM (出力時の標準)
 * * H:MM
 * * HHHMM
 * * HHMM
 * * HMM
 * * H
 * * .H
 * * H.H
 *
 * ### 内部形式
 * Duration
 *
 */

import { z } from 'zod'
import * as duration from 'duration-fns'

/********************************************
 * for TIME
 ********************************************/

const _timeHHHMM = Symbol('timeHHHMM') // never export this

// HHH:MM or HH:MM or H:MM or HHHMM or HHMM or HMM
const normalizeHHHMM = (v: string): string => {
  if (v.includes(':')) {
    // HHH:MM or HH:MM or H:MM
    return v.split(':')[0].length === 1 ? '0' + v : v
  } else {
    // HHHMM or HHMM or HMM
    return v.substring(0, v.length - 2) + ':' + v.substring(v.length - 2)
  }
}

// HHH:MM or HH:MM or H:MM or HHHMM or HHMM or HMM format
const hhhmmNotation = z.string().regex(/^[0-9]{1,3}:?[0-5][0-9]$/)

export const timeNotation = hhhmmNotation.transform(normalizeHHHMM).brand(_timeHHHMM)

export const timeHHHMM = timeNotation.parse

export const tryNormalizedTime = (v: string): string => {
  const result = timeNotation.safeParse(v)
  if (!result.success) return v
  return result.data
}

/********************************************
 * for Duration
 ********************************************/

const _durationHHHMM = Symbol('durationHHHMM') // never export this

// HHH:MM or HH:MM or H:MM or HHHMM or HHMM or HMM 形式から Duration に変換
// 例:
// - "9:30" → { hours: 9, minutes: 30 } (9時間30分)
// - "12:45" → { hours: 12, minutes: 45 } (12時間45分)
// - "123:15" → { hours: 123, minutes: 15 } (123時間15分)
// - "0930" → { hours: 9, minutes: 30 } (9時間30分)
const hhhmmToDuration = (v: string): Duration => {
  const [hh, mm] = normalizeHHHMM(v).split(':')
  return { hours: Number(hh), minutes: Number(mm) }
}

// 時間表記 (H.H + h形式) から Duration に変換
// 例:
// - "9.5h" → { hours: 9, minutes: 30 } (9時間30分)
// - "1.25h" → { hours: 1, minutes: 15 } (1時間15分)
// - "12.75h" → { hours: 12, minutes: 45 } (12時間45分)
// - "0.5h" → { hours: 0, minutes: 30 } (0時間30分)
const hoursToDuration = (v: string, ctx: z.RefinementCtx): Duration => {
  const n = Number(v.substring(0, v.length - 1))
  if (n >= 1000) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '1000時間未満で入力してください'
    })
    return z.NEVER
  }
  const hours = Math.trunc(n)
  const minutes = Math.trunc((n - hours) * 60)
  return { hours, minutes }
}

// 分表記 (M + m形式) から Duration に変換
// 例:
// - "30m" → { minutes: 30 } (0時間30分)
// - "90m" → { minutes: 90 } (1時間30分) ※正規化すると { hours: 1, minutes: 30 } になる
// - "120m" → { minutes: 120 } (2時間0分) ※正規化すると { hours: 2, minutes: 0 } になる
// - "45m" → { minutes: 45 } (0時間45分)
const minutesToDuration = (v: string, ctx: z.RefinementCtx): Duration => {
  const minutes = Number(v.substring(0, v.length - 1))
  if (minutes >= 1000 * 60) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '1000時間未満で入力してください'
    })
    return z.NEVER
  }
  return { minutes }
}

// Single digit を時間としてパースする（分は0として扱う）
// 例:
// - "9" → { hours: 9, minutes: 0 } (9時間0分)
// - "5" → { hours: 5, minutes: 0 } (5時間0分)
// - "12" → { hours: 12, minutes: 0 } (12時間0分)
// - "123" → { hours: 123, minutes: 0 } (123時間0分)
const singleDigitToDuration = (v: string): Duration => {
  const hours = Number(v)
  return { hours, minutes: 0 }
}

const decimalHoursNotation = z.string().regex(/^(\d+)?\.?(\d+)?h/)
const integerMinutesNotation = z.string().regex(/^\d+m/)
const singleDigitNotation = z.string().regex(/^\d+$/)

export const durationNotation = z
  .union([
    timeNotation.transform(hhhmmToDuration),
    decimalHoursNotation.transform(hoursToDuration),
    integerMinutesNotation.transform(minutesToDuration),
    singleDigitNotation.transform(singleDigitToDuration)
  ])
  .brand(_durationHHHMM)

export const durationHHHMM = durationNotation.parse

export const tryNormalizedDuration = (v: string): string => {
  const result = durationNotation.safeParse(v)
  if (!result.success) return v
  return durationToHHHMM(result.data)
}

export const durationToHHHMM = (d: Duration): string => {
  const nd = duration.normalize(d)
  const hourPart = Math.trunc(duration.toHours(nd))
  return String(hourPart) + ':' + String(nd.minutes).padStart(2, '0')
}

/**
 * normalizeされたdurationはdaysまで変換されているので、hoursに戻す
 */
export const normalizedDutationToHHMMDuration = (dhm: duration.Duration): duration.Duration => {
  if (dhm.days === 0) return dhm
  return {
    years: 0,
    months: 0,
    weeks: 0,
    days: 0,
    hours: dhm.days * 24 + dhm.hours,
    minutes: dhm.minutes,
    seconds: 0,
    milliseconds: 0
  }
}
